import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { mainWebsiteUrl, categories } from "@/mocks/categories";

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // When scrolled or the mobile menu is open, use a solid header with dark text
  const solid = scrolled || menuOpen;
  const linkColor = solid
    ? "text-foreground-700 hover:text-primary-700"
    : "text-white/90 hover:text-white";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-white border-b border-background-300"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-lg ${
              solid ? "bg-primary-500 text-white" : "bg-white/20 text-white"
            }`}
          >
            <i className="ri-grid-line w-5 h-5 flex items-center justify-center text-lg" />
          </span>
          <span
            className={`font-heading text-lg font-bold whitespace-nowrap ${
              solid ? "text-foreground-950" : "text-white"
            }`}
          >
            {t("brand.name")}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              location.pathname === "/" ? "bg-primary-500 text-white" : linkColor
            }`}
          >
            {t("nav.home")}
          </Link>
          <Link
            to="/guide/usfans-spreadsheet-guide"
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              location.pathname === "/guide/usfans-spreadsheet-guide" ? "bg-primary-500 text-white" : linkColor
            }`}
          >
            {t("nav.guide")}
          </Link>
          <a
            href={mainWebsiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer ${linkColor}`}
          >
            {t("nav.shopping")}
          </a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher light={!solid} />
          <a
            href={mainWebsiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-full bg-accent-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
          >
            {t("nav.visit")}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className={`md:hidden flex h-10 w-10 items-center justify-center rounded-lg cursor-pointer ${
            solid ? "text-foreground-950" : "text-white"
          }`}
          aria-label="Menu"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} w-6 h-6 flex items-center justify-center text-xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 max-h-[75vh] overflow-y-auto px-4 py-4">
          <nav className="flex flex-col gap-1">
            <Link
              to="/"
              className="rounded-lg px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              {t("nav.home")}
            </Link>
            <Link
              to="/guide/usfans-spreadsheet-guide"
              className="rounded-lg px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer"
            >
              {t("nav.guide")}
            </Link>
            <a
              href={mainWebsiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="rounded-lg px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t("nav.shopping")}
            </a>
          </nav>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t("nav.categories")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 hover:border-primary-400 hover:bg-primary-50"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-base`} />
                  </span>
                  <span className="truncate whitespace-nowrap">{t(cat.key)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <LanguageSwitcher />
            <a
              href={mainWebsiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-full bg-accent-500 px-5 py-2 text-sm font-semibold text-white"
            >
              {t("nav.visit")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}